import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { CalendarDays, ChevronLeft, ChevronRight, MessageCircle, Printer, Syringe, MapPin, Stethoscope, Users, Trash2, Euro } from 'lucide-react';
import { PageHeader } from '../components/Common';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  listAgendas, listCitas, getQuirofanoDia, setQuirofanoDia, addDaysStr, dateToStr, strToDate, todayStr,
} from '../lib/citasApi';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { isAdmin } from '../lib/roles';
import CatalogCombobox from '../components/qx/CatalogCombobox';
import AltaProfesionalDialog from '../components/qx/AltaProfesionalDialog';
import { MENSAJE_COLEGIADO, etiquetaColegiado, normalizarColegiado } from '../lib/medicos';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';

// Categorías de honorarios del anestesista (solo admin). En euros. Dos son
// del producto; las demás las declara cada clínica en Configuración
// (`categorias_honorarios_anestesia`, separadas por comas). Una categoría que
// ya tenga cifras guardadas se sigue enseñando aunque se quite de la lista,
// para que nadie pierda de vista un importe.
const HONOR_CATS_BASE = [
  { key: 'modulo', label: 'Por módulo' },
  { key: 'externos', label: 'Cirugía · externos' },
];

const claveCategoria = (nombre) => nombre
  .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  .toLowerCase().trim().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 40);

function categoriasHonorarios(configuradas, items) {
  const cats = [...HONOR_CATS_BASE];
  const vistas = new Set(cats.map((c) => c.key));
  const nombres = String(configuradas || '').split(',').map((n) => n.trim()).filter(Boolean);
  for (const nombre of nombres) {
    const key = claveCategoria(nombre);
    if (key && !vistas.has(key)) { vistas.add(key); cats.push({ key, label: `Cirugía · ${nombre}` }); }
  }
  for (const it of items) {
    for (const key of Object.keys(it.honorarios || {})) {
      if (!vistas.has(key)) { vistas.add(key); cats.push({ key, label: key.replace(/_/g, ' ') }); }
    }
  }
  return cats;
}

function monthBounds(month) {
  const first = strToDate(`${month}-01`);
  const last = new Date(first.getFullYear(), first.getMonth() + 1, 0);
  return { from: dateToStr(first), to: dateToStr(last) };
}

function moveMonth(month, amount) {
  const current = strToDate(`${month}-01`);
  current.setMonth(current.getMonth() + amount);
  return dateToStr(current).slice(0, 7);
}

function monthLabel(month) {
  return strToDate(`${month}-01`).toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
}

function dayLabel(date) {
  return strToDate(date).toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' });
}

function planningText(month, rows) {
  const lines = [`Planning de anestesia · ${monthLabel(month)}`];
  for (const row of rows) {
    const pacientes = row.pacientes.map((p) => `${p.start || '—'} ${p.name}`).join(' · ');
    lines.push(`${dayLabel(row.date)} · Inicio ${row.start || '—'} · ${row.agenda_name}${row.lugar ? ` (${row.lugar})` : ''} · ${row.anestesista || 'Sin asignar'}`);
    if (pacientes) lines.push(`Pacientes: ${pacientes}`);
  }
  return lines.join('\n');
}

function escapePrint(value) {
  return String(value || '').replace(/[&<>"']/g, (char) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;',
  }[char]));
}

function printPlanning(month, rows) {
  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    toast.error('El navegador ha bloqueado la ventana de impresión');
    return;
  }
  printWindow.opener = null;
  const body = rows.map((row) => `
    <tr>
      <td>${escapePrint(dayLabel(row.date))}</td>
      <td>${escapePrint(row.start || '—')}</td>
      <td>${escapePrint(row.agenda_name)}</td>
      <td>${escapePrint(row.lugar || '—')}</td>
      <td>${escapePrint(row.anestesista || 'Sin asignar')}</td>
      <td>${row.pacientes.map((p) => `${escapePrint(p.start || '—')} · ${escapePrint(p.name)}`).join('<br>')}</td>
    </tr>`).join('');
  printWindow.document.write(`<!doctype html><html lang="es"><head><meta charset="utf-8"><title>Planning de anestesia</title>
    <style>body{font:13px Arial,sans-serif;color:#13231E;margin:28px}h1{font-size:22px;margin:0 0 4px}p{color:#52625d;margin:0 0 20px}table{width:100%;border-collapse:collapse}th,td{border-bottom:1px solid #d9e3dd;padding:9px 7px;text-align:left;vertical-align:top}th{font-size:11px;text-transform:uppercase;color:#52625d}td:nth-child(2){font-weight:700;white-space:nowrap}@media print{body{margin:12mm}}</style>
    </head><body><h1>Planning de anestesia</h1><p>${escapePrint(monthLabel(month))} · ${rows.length} módulo(s)</p>
    <table><thead><tr><th>Fecha</th><th>Inicio</th><th>Quirófano</th><th>Lugar</th><th>Anestesista</th><th>Pacientes</th></tr></thead><tbody>${body}</tbody></table>
    <script>window.onload=()=>window.print()<\/script></body></html>`);
  printWindow.document.close();
}

/**
 * Panel de administración de anestesistas y sus honorarios (2026-07-16).
 * SOLO para administradores: añadir/quitar
 * anestesistas y fijar sus honorarios por las 4 categorías. El backend
 * (require_role admin) vuelve a comprobarlo y oculta los honorarios al resto.
 *
 * 2026-09-08 (punto 7): el alta va con el formato obligatorio
 * «Dr./Dra. Nombre Apellido1 Apellido2» + nº de colegiado
 * (AltaProfesionalDialog) y cada fila enseña su colegiado, editable aquí
 * para los que están «pendiente» (PUT /api/qx-catalogo/{id}).
 */
function AnestesistasHonorarios() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [altaOpen, setAltaOpen] = useState(false);
  const [drafts, setDrafts] = useState({}); // id -> {<categoría>: importe, colegiado}
  const [savingId, setSavingId] = useState(null);
  const [honorCats, setHonorCats] = useState(HONOR_CATS_BASE);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/qx-catalogo', { params: { tipo: 'anestesista' } });
      const arr = Array.isArray(res.data) ? res.data : [];
      // Las categorías de la clínica; si Configuración no se deja leer,
      // quedan las del producto y las que ya tengan cifras.
      let configuradas = '';
      try {
        const aj = await api.get('/api/settings');
        configuradas = aj?.data?.categorias_honorarios_anestesia || '';
      } catch { /* sin categorías propias */ }
      const cats = categoriasHonorarios(configuradas, arr);
      setHonorCats(cats);
      setItems(arr);
      const d = {};
      for (const it of arr) {
        const h = it.honorarios || {};
        d[it.id] = { colegiado: it.colegiado ?? '' };
        for (const c of cats) d[it.id][c.key] = h[c.key] ?? '';
      }
      setDrafts(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudieron cargar los anestesistas');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  const setDraft = (id, key, val) => setDrafts((p) => ({ ...p, [id]: { ...p[id], [key]: val } }));

  const guardar = async (id) => {
    setSavingId(id);
    try {
      const d = drafts[id] || {};
      const item = items.find((it) => it.id === id) || {};
      // Colegiado: se guarda si ha cambiado (vacío = vuelve a «pendiente»).
      const colegiadoDraft = (d.colegiado ?? '').toString().trim();
      if (colegiadoDraft !== (item.colegiado || '')) {
        if (colegiadoDraft && !normalizarColegiado(colegiadoDraft)) {
          toast.error(MENSAJE_COLEGIADO);
          return;
        }
        const r = await api.put(`/api/qx-catalogo/${id}`, { colegiado: colegiadoDraft || null });
        setItems((prev) => prev.map((it) => (it.id === id ? { ...it, colegiado: r.data?.colegiado ?? null } : it)));
        setDraft(id, 'colegiado', r.data?.colegiado ?? '');
      }
      const payload = {};
      for (const c of honorCats) {
        const raw = d[c.key];
        payload[c.key] = raw === '' || raw == null ? null : Number(raw);
      }
      await api.put(`/api/qx-catalogo/${id}/honorarios`, payload);
      toast.success('Guardado');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo guardar');
    } finally { setSavingId(null); }
  };

  const quitar = async (id, nombre) => {
    if (!window.confirm(`¿Quitar a ${nombre} de la lista de anestesistas?`)) return;
    try {
      await api.delete(`/api/qx-catalogo/${id}`);
      toast.success('Anestesista quitado');
      cargar();
    } catch (e) { toast.error(e?.response?.data?.detail || 'No se pudo quitar'); }
  };

  return (
    <div className="cp-card p-4 mb-4" data-testid="anestesistas-honorarios">
      <div className="flex items-center gap-2 mb-1">
        <Euro className="w-4 h-4 text-[#2E6E5E]" />
        <h2 className="font-semibold text-[#13231E]">Anestesistas y honorarios</h2>
        <span className="text-xs text-muted-foreground">· Solo administradores</span>
      </div>
      <p className="text-xs text-muted-foreground mb-3">
        Gestiona quién es anestesista y sus honorarios (€). Estas cifras solo las ven los administradores.
        Los nombres van como «Dr./Dra. Nombre Apellido1 Apellido2» con su nº de colegiado.
      </p>

      <div className="flex items-center gap-2 mb-3">
        <Button onClick={() => setAltaOpen(true)} className="h-9 bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="anestesista-nuevo">
          Dar de alta un anestesista
        </Button>
        <AltaProfesionalDialog
          open={altaOpen}
          onOpenChange={setAltaOpen}
          tipo="anestesista"
          onCreated={() => cargar()}
        />
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground py-4">Cargando…</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-muted-foreground py-4">Aún no hay anestesistas. Añade el primero arriba.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-muted-foreground border-b border-[#D9E3DD]">
                <th className="py-2 pr-3 font-medium">Anestesista</th>
                <th className="py-2 px-2 font-medium whitespace-nowrap">Colegiado</th>
                {honorCats.map((c) => (
                  <th key={c.key} className="py-2 px-2 font-medium whitespace-nowrap">{c.label} (€)</th>
                ))}
                <th className="py-2 pl-2"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-b border-[#EEF2F0]">
                  <td className="py-2 pr-3 font-medium text-[#13231E] whitespace-nowrap" data-testid={`hon-nombre-${it.id}`}>{it.nombre}</td>
                  <td className="py-1.5 px-2">
                    <Input
                      inputMode="numeric"
                      className="h-8 w-32 tabular-nums"
                      value={drafts[it.id]?.colegiado ?? ''}
                      placeholder={etiquetaColegiado(it.colegiado)}
                      title={it.colegiado ? `Colegiado ${it.colegiado}` : 'Colegiado pendiente'}
                      onChange={(e) => setDraft(it.id, 'colegiado', e.target.value)}
                      data-testid={`hon-${it.id}-colegiado`}
                    />
                  </td>
                  {honorCats.map((c) => (
                    <td key={c.key} className="py-1.5 px-2">
                      <Input
                        type="number" step="0.01" min="0"
                        className="h-8 w-24 tabular-nums"
                        value={drafts[it.id]?.[c.key] ?? ''}
                        onChange={(e) => setDraft(it.id, c.key, e.target.value)}
                        data-testid={`hon-${it.id}-${c.key}`}
                      />
                    </td>
                  ))}
                  <td className="py-1.5 pl-2 whitespace-nowrap">
                    <Button
                      size="sm"
                      className="h-8 bg-[#2E6E5E] hover:bg-[#1E4A3F]"
                      disabled={savingId === it.id}
                      onClick={() => guardar(it.id)}
                      data-testid={`hon-save-${it.id}`}
                    >
                      {savingId === it.id ? '…' : 'Guardar'}
                    </Button>
                    <button
                      type="button"
                      className="ml-2 align-middle text-muted-foreground hover:text-red-600"
                      title="Quitar anestesista"
                      onClick={() => quitar(it.id, it.nombre)}
                      data-testid={`hon-del-${it.id}`}
                    >
                      <Trash2 className="w-4 h-4 inline" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * Agenda de Anestesia — planificar y controlar anestesistas.
 *
 * Para un día, muestra cada quirófano (agenda tipo "quirofano") con citas ese
 * día: su lugar, el anestesista asignado (editable — se guarda por
 * quirófano/día en la config de quirófano) y sus pacientes. Arriba, resumen
 * de la carga de cada anestesista.
 */
export default function AnestesiaPage() {
  const { user } = useAuth();
  const admin = isAdmin(user);
  const hoy = todayStr;
  const [view, setView] = useState('day');
  const [date, setDate] = useState(hoy);
  const [month, setMonth] = useState(() => hoy().slice(0, 7));
  const [rows, setRows] = useState([]); // [{agenda, lugar, anestesista, pacientes:[...]}]
  const [monthRows, setMonthRows] = useState([]);
  const [monthAnestesista, setMonthAnestesista] = useState('all');
  const [loading, setLoading] = useState(true);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      if (view === 'month') {
        const { from, to } = monthBounds(month);
        const res = await api.get('/api/agendas/quirofano/planificacion', {
          params: { date_from: from, date_to: to },
        });
        setMonthRows(Array.isArray(res.data) ? res.data : []);
        return;
      }
      const ags = await listAgendas();
      const qx = (Array.isArray(ags) ? ags : []).filter((a) => (a.tipo || '') === 'quirofano');
      const res = await Promise.all(qx.map(async (a) => {
        const [citas, cfg] = await Promise.all([
          listCitas({ agendaId: a.id, date }).catch(() => []),
          getQuirofanoDia(a.id, date).catch(() => ({})),
        ]);
        const pacientes = (Array.isArray(citas) ? citas : [])
          .sort((x, y) => (x.start || '').localeCompare(y.start || ''))
          .map((c) => ({ id: c.id, name: c.patient_name, proc: c.procedimiento || c.concept_name || '', time: c.start, notes: c.notes || '' }));
        return {
          agenda: a,
          lugar: cfg?.lugar || (Array.isArray(citas) && citas.find((c) => c.lugar)?.lugar) || '',
          anestesista: cfg?.anestesista || (Array.isArray(citas) && citas.find((c) => c.anestesista)?.anestesista) || '',
          pacientes,
        };
      }));
      // Solo quirófanos con pacientes ese día.
      setRows(res.filter((r) => r.pacientes.length > 0));
    } catch (e) {
      toast.error(e?.response?.data?.detail || `No se pudo cargar la anestesia del ${view === 'month' ? 'mes' : 'día'}`);
    } finally {
      setLoading(false);
    }
  }, [date, month, view]);

  useEffect(() => { cargar(); }, [cargar]);

  const guardarAnestesista = async (row, nombre) => {
    try {
      await setQuirofanoDia(row.agenda.id, { date, anestesista: nombre || '' });
      setRows((prev) => prev.map((r) => (r.agenda.id === row.agenda.id ? { ...r, anestesista: nombre } : r)));
      toast.success(nombre ? `Anestesista asignado: ${nombre}` : 'Anestesista quitado');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo asignar el anestesista');
    }
  };

  // Resumen por anestesista (cuántos quirófanos / pacientes cubre ese día).
  const resumen = useMemo(() => {
    const m = {};
    for (const r of rows) {
      const key = r.anestesista || '— Sin asignar —';
      if (!m[key]) m[key] = { quirofanos: 0, pacientes: 0 };
      m[key].quirofanos += 1;
      m[key].pacientes += r.pacientes.length;
    }
    return Object.entries(m).sort((a, b) => b[1].pacientes - a[1].pacientes);
  }, [rows]);

  const anestesistasMes = useMemo(
    () => [...new Set(monthRows.map((row) => row.anestesista).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'es')),
    [monthRows],
  );
  const rowsMesFiltrados = useMemo(
    () => monthRows.filter((row) => monthAnestesista === 'all' || row.anestesista === monthAnestesista),
    [monthRows, monthAnestesista],
  );
  const resumenMes = useMemo(() => {
    const resumenPorAnestesista = {};
    for (const row of rowsMesFiltrados) {
      const nombre = row.anestesista || 'Sin asignar';
      if (!resumenPorAnestesista[nombre]) resumenPorAnestesista[nombre] = { modulos: 0, pacientes: 0 };
      resumenPorAnestesista[nombre].modulos += 1;
      resumenPorAnestesista[nombre].pacientes += row.pacientes.length;
    }
    return Object.entries(resumenPorAnestesista).sort((a, b) => b[1].modulos - a[1].modulos);
  }, [rowsMesFiltrados]);

  const enviarPlanningWhatsapp = () => {
    if (!rowsMesFiltrados.length) return;
    window.open(`https://wa.me/?text=${encodeURIComponent(planningText(month, rowsMesFiltrados))}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <div>
      <PageHeader title="Agenda de anestesia" subtitle={view === 'month' ? 'Planning mensual de módulos de quirófano' : 'Anestesista asignado por quirófano y día'} />

      {/* Panel admin: gestión de anestesistas + honorarios (solo administradores) */}
      {admin && <AnestesistasHonorarios />}

      <Tabs value={view} onValueChange={setView} className="mb-4">
        <TabsList>
          <TabsTrigger value="day" data-testid="anestesia-vista-dia">Día</TabsTrigger>
          <TabsTrigger value="month" data-testid="anestesia-vista-mes"><CalendarDays className="w-4 h-4 mr-1.5" />Mes</TabsTrigger>
        </TabsList>
      </Tabs>

      {view === 'day' && (
        <div className="flex items-center gap-1.5 mb-4">
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-44" data-testid="anestesia-fecha" />
          <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => setDate((d) => addDaysStr(d || hoy(), -1))} aria-label="Día anterior">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button variant="outline" className="h-9" onClick={() => setDate(hoy())}>Hoy</Button>
          <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => setDate((d) => addDaysStr(d || hoy(), 1))} aria-label="Día siguiente">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      )}

      {view === 'month' && (
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => setMonth((value) => moveMonth(value, -1))} aria-label="Mes anterior">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-40" data-testid="anestesia-mes" />
          <Button variant="outline" className="h-9" onClick={() => setMonth(hoy().slice(0, 7))}>Este mes</Button>
          <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => setMonth((value) => moveMonth(value, 1))} aria-label="Mes siguiente">
            <ChevronRight className="w-4 h-4" />
          </Button>
          <div className="grow" />
          <select
            value={monthAnestesista}
            onChange={(e) => setMonthAnestesista(e.target.value)}
            className="h-9 max-w-full rounded-md border border-input bg-background px-3 text-sm"
            aria-label="Filtrar planning por anestesista"
            data-testid="anestesia-mes-anestesista"
          >
            <option value="all">Todos los anestesistas</option>
            {anestesistasMes.map((nombre) => <option key={nombre} value={nombre}>{nombre}</option>)}
          </select>
          <Button variant="outline" className="h-9" onClick={() => printPlanning(month, rowsMesFiltrados)} disabled={!rowsMesFiltrados.length}>
            <Printer className="w-4 h-4 mr-1.5" />Imprimir
          </Button>
          <Button className="h-9 bg-[#2E6E5E] hover:bg-[#1E4A3F]" onClick={enviarPlanningWhatsapp} disabled={!rowsMesFiltrados.length}>
            <MessageCircle className="w-4 h-4 mr-1.5" />WhatsApp
          </Button>
        </div>
      )}

      {loading ? (
        <div className="cp-card p-8 text-center text-sm text-muted-foreground">Cargando…</div>
      ) : view === 'month' ? (
        rowsMesFiltrados.length === 0 ? (
          <div className="cp-card p-8 text-center text-sm text-muted-foreground">No hay módulos de quirófano programados en {monthLabel(month)}.</div>
        ) : (
          <div className="space-y-4" data-testid="anestesia-planning-mensual">
            <div className="flex flex-wrap gap-2">
              {resumenMes.map(([nombre, r]) => (
                <div key={nombre} className="inline-flex items-center gap-2 rounded-full border border-[#D9E3DD] bg-white px-3 py-1.5 text-sm">
                  <Syringe className="w-3.5 h-3.5 text-[#2E6E5E]" />
                  <span className="font-medium text-[#13231E]">{nombre}</span>
                  <span className="text-muted-foreground">· {r.modulos} módulo(s) · {r.pacientes} paciente(s)</span>
                </div>
              ))}
            </div>
            <div className="cp-card overflow-x-auto">
              <table className="w-full min-w-[760px] text-sm">
                <thead className="border-b border-[#D9E3DD] bg-[rgba(46,110,94,0.04)] text-left text-xs text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3 font-medium">Fecha</th>
                    <th className="px-3 py-3 font-medium">Inicio</th>
                    <th className="px-3 py-3 font-medium">Quirófano</th>
                    <th className="px-3 py-3 font-medium">Lugar</th>
                    <th className="px-3 py-3 font-medium">Anestesista</th>
                    <th className="px-4 py-3 font-medium">Pacientes</th>
                  </tr>
                </thead>
                <tbody>
                  {rowsMesFiltrados.map((row) => (
                    <tr key={row.id} className="border-b border-[#EEF2F0] last:border-0">
                      <td className="px-4 py-3 capitalize whitespace-nowrap font-medium text-[#13231E]">{dayLabel(row.date)}</td>
                      <td className="px-3 py-3 tabular-nums whitespace-nowrap font-semibold text-[#13231E]">{row.start || '—'}</td>
                      <td className="px-3 py-3 whitespace-nowrap"><span className="inline-flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: row.agenda_color || '#2E6E5E' }} />{row.agenda_name}</span></td>
                      <td className="px-3 py-3 whitespace-nowrap text-muted-foreground">{row.lugar || '—'}</td>
                      <td className="px-3 py-3 whitespace-nowrap">{row.anestesista || <span className="text-amber-700">Sin asignar</span>}</td>
                      <td className="px-4 py-3 text-xs leading-5">{row.pacientes.map((p) => <div key={p.id}><span className="tabular-nums text-muted-foreground">{p.start || '—'}</span> <span className="font-medium text-[#13231E]">{p.name}</span>{p.procedimiento ? <span className="text-muted-foreground"> · {p.procedimiento}</span> : null}</div>)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )
      ) : rows.length === 0 ? (
        <div className="cp-card p-8 text-center text-sm text-muted-foreground">No hay quirófano programado para esa fecha.</div>
      ) : (
        <div className="space-y-4">
          {/* Resumen por anestesista */}
          <div className="flex flex-wrap gap-2">
            {resumen.map(([nombre, r]) => (
              <div key={nombre} className="inline-flex items-center gap-2 rounded-full border border-[#D9E3DD] bg-white px-3 py-1.5 text-sm">
                <Syringe className="w-3.5 h-3.5 text-[#2E6E5E]" />
                <span className="font-medium text-[#13231E]">{nombre}</span>
                <span className="text-muted-foreground">· {r.quirofanos} quirófano(s) · {r.pacientes} paciente(s)</span>
              </div>
            ))}
          </div>

          {/* Un bloque por quirófano */}
          {rows.map((row) => (
            <div key={row.agenda.id} className="cp-card p-4" data-testid={`anestesia-quirofano-${row.agenda.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                <div className="flex flex-wrap items-center gap-2 min-w-0">
                  <span className="w-2.5 h-2.5 rounded-full inline-block shrink-0" style={{ backgroundColor: row.agenda.color || '#2E6E5E' }} />
                  <span className="font-semibold text-[#13231E] truncate">{row.agenda.name}</span>
                  {row.lugar && (
                    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                      <MapPin className="w-3 h-3 text-[#2E6E5E]" /> {row.lugar}
                    </span>
                  )}
                  <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                    <Users className="w-3 h-3 text-[#2E6E5E]" /> {row.pacientes.length} paciente(s)
                  </span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-xs text-muted-foreground inline-flex items-center gap-1"><Syringe className="w-3.5 h-3.5" /> Anestesista:</span>
                  <div className="w-56">
                    <CatalogCombobox
                      tipo="anestesista"
                      value={row.anestesista}
                      onChange={(nombre) => guardarAnestesista(row, nombre)}
                      placeholder="Asignar anestesista…"
                      data-testid={`anestesia-select-${row.agenda.id}`}
                    />
                  </div>
                </div>
              </div>
              <ol className="space-y-1">
                {row.pacientes.map((p, i) => (
                  <li key={p.id} className="text-sm text-foreground flex gap-2">
                    <span className="tabular-nums text-muted-foreground shrink-0">{p.time || (i + 1)}</span>
                    <span className="min-w-0">
                      <span className="font-medium text-[#13231E]">{p.name}</span>
                      {(p.proc || p.notes) && <span className="text-muted-foreground"> — {p.notes || p.proc}</span>}
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
